import { useEffect, useState } from 'react'
import { api, ApiError, prettyPhone, type Contact } from '../lib/api'
import {
  AddressFields, BLANK_ADDRESS, addressBody, type Address,
} from './AddressFields'

/** Saving a number from the call log: onto a NEW caller, or onto one already
 *  in the book.
 *
 *  WHY BOTH, AND WHY THE SECOND ONE MATTERS MORE
 *
 *  The same man rings from his mobile, then from the office, then from the
 *  house. With only "save as a new contact", the second and third numbers each
 *  create another record for a person already in the book — and then his
 *  history splits three ways, the caller-ID pop shows whichever duplicate
 *  happens to match, and the notes somebody wrote on the first one are invisible
 *  when he rings from the second.
 *
 *  So the existing-caller path is the default when a name is typed that matches
 *  somebody: search first, attach second, and creating a new person is what you
 *  do when nobody matches.
 */
export function SaveNumber({
  phone, suggestedName, onDone, onCancel,
}: {
  phone: string
  /** What the PBX said the caller was called, if anything. A starting point,
   *  not an answer. */
  suggestedName?: string
  onDone: (contact: Contact) => void
  onCancel: () => void
}) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [q, setQ] = useState(suggestedName ?? '')
  const [matches, setMatches] = useState<Contact[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [label, setLabel] = useState('')
  const [confirmMove, setConfirmMove] = useState<{ id: string; who: string } | null>(null)

  // New-caller fields.
  const [form, setForm] = useState({
    display_name: suggestedName ?? '', language: '', notes: '',
  })
  const [address, setAddress] = useState<Address>({ ...BLANK_ADDRESS })

  // NOTHING IS FETCHED UNTIL SOMETHING IS TYPED. An empty term is not a
  // search -- it lists the whole book, which is `phonebook.view` and is not a
  // dispatcher's to browse. It also contradicted the screen's own empty state
  // ("Start typing a name") by quietly rendering the first eight people in the
  // organization, names and every number, before a key was pressed.
  //
  // AND A REFUSAL IS NOT "NOBODY MATCHES THAT". The catch used to collapse
  // every error into an empty list, so a 403 on `search.callers` read as "this
  // caller is not in the book" and steered the dispatcher into creating the
  // duplicate this whole screen exists to prevent.
  useEffect(() => {
    if (mode !== 'existing') return
    const term = q.trim()
    if (!term) { setMatches([]); return }
    const t = setTimeout(() => {
      api.contacts(term)
        .then((found) => { setMatches(found); setError(null) })
        .catch((e) => {
          setMatches([])
          setError(e instanceof ApiError ? e.message
            : 'Could not look that up. Check the connection before deciding '
              + 'they are not in the book.')
        })
    }, 200)
    return () => clearTimeout(t)
  }, [q, mode])

  async function attach(contact: Contact, move = false) {
    setBusy(true)
    setError(null)
    try {
      const updated = await api.contactAddPhone(contact.id, {
        phone,
        label: label.trim() || null,
        // Not primary: the number they already had is the one they are known
        // by. Promoting a number captured from one call over one somebody
        // deliberately entered would be the wrong default.
        make_primary: false,
        move,
      })
      onDone(updated)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Held by somebody else. Say whose it is and let the operator decide —
        // a number changing hands silently is how a caller's history quietly
        // leaves his record.
        setConfirmMove({ id: contact.id, who: e.message })
      } else {
        setError(e instanceof ApiError ? e.message : 'Could not add that number.')
      }
    } finally {
      setBusy(false)
    }
  }

  async function createNew(e: React.FormEvent) {
    e.preventDefault()
    if (!form.display_name.trim()) return
    setBusy(true)
    setError(null)
    try {
      const saved = await api.contactCreate({
        display_name: form.display_name.trim(),
        phone,
        ...addressBody(address),
        language: form.language.trim() || null,
        notes: form.notes.trim() || null,
        source: 'call',
      })
      onDone(saved)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that caller.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="savenumber">
      <div className="sn-head">
        <span className="mono">{prettyPhone(phone)}</span>
        <div className="tabs">
          <button
            className={`tab ${mode === 'existing' ? 'on' : ''}`}
            onClick={() => setMode('existing')}
          >
            Add to someone already here
          </button>
          <button
            className={`tab ${mode === 'new' ? 'on' : ''}`}
            onClick={() => setMode('new')}
          >
            New caller
          </button>
        </div>
        <span className="spacer" style={{ marginLeft: 'auto' }} />
        <button className="ghost tiny" onClick={onCancel}>Cancel</button>
      </div>

      {error && <div className="notice">{error}</div>}

      {confirmMove && (
        <div className="notice">
          {/* NOT the calls. `_add_number` re-files only rows whose
              `contact_id IS NULL`; anything already filed to that person stays
              with them, deliberately, so attaching a number cannot quietly
              take somebody's history. This said the opposite for as long as it
              has existed. */}
          {confirmMove.who} Moving it will take the NUMBER off that record and
          put it on this one. The calls already filed under their name stay
          with them — this does not move history.
          <div className="btnrow">
            <button
              className="ghost danger"
              onClick={() => {
                const c = matches.find((m) => m.id === confirmMove.id)
                setConfirmMove(null)
                if (c) attach(c, true)
              }}
            >
              Move it here
            </button>
            <button className="ghost" onClick={() => setConfirmMove(null)}>
              Leave it where it is
            </button>
          </div>
        </div>
      )}

      {mode === 'existing' ? (
        <div className="sn-body">
          <div className="grid2">
            <div className="field">
              <label>Who is this?</label>
              <input
                autoFocus
                value={q}
                placeholder="Name, or any of their numbers"
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="field">
              <label>What is this number — optional</label>
              <input
                value={label}
                placeholder="mobile, office, home"
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
          </div>

          {matches.length === 0 ? (
            <div className="listempty">
              {q.trim()
                ? 'Nobody matches that. Use “New caller” if they are not in the book.'
                : 'Start typing a name.'}
            </div>
          ) : (
            matches.slice(0, 8).map((c) => (
              <div className="listrow" key={c.id}>
                <span className="nm">{c.display_name}</span>
                {/* Their existing numbers, so the operator can see this is the
                    right person before attaching a fourth one to him. */}
                <span className="dim">
                  {c.phones.length === 0
                    ? 'no numbers yet'
                    : c.phones
                        .map((p) => prettyPhone(p.phone_e164) + (p.label ? ` (${p.label})` : ''))
                        .join(' · ')}
                </span>
                <span className="spacer" style={{ marginLeft: 'auto' }} />
                <button
                  className="ghost tiny"
                  disabled={busy}
                  onClick={() => attach(c)}
                >
                  Add this number
                </button>
              </div>
            ))
          )}
        </div>
      ) : (
        <form className="sn-body" onSubmit={createNew}>
          <div className="grid2">
            <div className="field">
              <label>Name</label>
              <input
                autoFocus
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label>Language</label>
              <input
                value={form.language}
                placeholder="Yiddish"
                onChange={(e) => setForm({ ...form, language: e.target.value })}
              />
            </div>
          </div>
          {/* The moment somebody is on the phone giving their address is the
              moment to record it, and it was being thrown away. */}
          <div className="field">
            <label>Where they live</label>
            <AddressFields value={address} onChange={setAddress} />
          </div>
          <div className="field">
            <label>Notes — what the next dispatcher should know</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="btnrow">
            <button className="primary compact"
                    disabled={busy || !form.display_name.trim()}>
              {busy ? 'Saving…' : 'Save to phone book'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
